import { useEffect, useRef, useState } from 'react';
import {
  getAidaTypewriterVisible,
  startAidaTypewriter,
  stopAidaTypewriter,
  subscribeAidaTypewriter,
} from '@/utils/aidaTypewriterEngine';

export type SmoothTypewriterOptions = {
  /** Wait after the first target characters arrive before showing anything. */
  startDelayMs?: number;
  /** Average ms per character (lower = faster). */
  msPerChar?: number;
  active: boolean;
  /** Unique ID for this stream — used to resume position after chat switch. */
  streamId?: string;
  /** Called once visible text has caught up while active is still true. */
  onCaughtUp?: () => void;
};

export function clearTypewriterPosition(streamId: string) {
  stopAidaTypewriter(streamId);
}

/**
 * Smooth letter-by-letter reveal. The animation lives in a module-level engine so it
 * keeps running if the chat unmounts (navigating to another page) or the conversation is switched.
 */
export function useSmoothTypewriter(target: string, options: SmoothTypewriterOptions) {
  const { active, startDelayMs, msPerChar, streamId, onCaughtUp } = options;

  const targetRef = useRef(target);
  const onCaughtUpRef = useRef(onCaughtUp);
  targetRef.current = target;
  onCaughtUpRef.current = onCaughtUp;

  const [visible, setVisible] = useState(() =>
    streamId && active ? getAidaTypewriterVisible(streamId, target) : '',
  );

  useEffect(() => {
    if (!active || !streamId) return;

    const detachCaughtUp = startAidaTypewriter({
      streamId,
      source: 'view',
      getTarget: () => targetRef.current,
      onCaughtUp: () => onCaughtUpRef.current?.(),
      startDelayMs,
      msPerChar,
    });

    setVisible(getAidaTypewriterVisible(streamId, targetRef.current));
    const unsubscribe = subscribeAidaTypewriter(streamId, setVisible);
    return () => {
      unsubscribe();
      detachCaughtUp();
    };
  }, [active, streamId, startDelayMs, msPerChar]);

  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  return { visible, visibleRef };
}
